import React, { useState, useCallback, createContext, useContext, useEffect } from 'react';

// Chave para sessionStorage
const STORAGE_KEY = 'debug_console_logs';
const DEBUG_ENABLED_KEY = 'debug_console_enabled';

// Para ativar/desativar o debug, use no console do navegador:
// localStorage.setItem('debug_console_enabled', 'true')  // ativar
// localStorage.setItem('debug_console_enabled', 'false') // desativar

// ============================================================
// API GLOBAL - Pode ser chamada de qualquer lugar (não apenas React)
// ============================================================

// Array global de listeners para notificar o React quando houver novos logs
let globalListeners = [];

// Verifica se o debug está habilitado
const isDebugEnabled = () => {
    const storageValue = localStorage.getItem(DEBUG_ENABLED_KEY);
    if (storageValue !== null) {
        return storageValue === 'true';
    }
    // Por padrão, ativo em desenvolvimento
    return process.env.NODE_ENV === 'development';
};

// Carrega logs do sessionStorage
const loadLogsFromStorage = () => {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
};

// Salva logs no sessionStorage
const saveLogsToStorage = (logs) => {
    try {
        const logsToSave = logs.slice(-100);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(logsToSave));
    } catch {
        // Ignora erros de storage
    }
};

// Logs armazenados globalmente
let globalLogs = loadLogsFromStorage();

// Função para adicionar log (pode ser chamada de qualquer lugar)
const addGlobalLog = (message, type = 'info') => {
    if (!isDebugEnabled()) return;

    const time = new Date().toLocaleTimeString();
    const logEntry = { message: String(message), time, type };

    globalLogs = [...globalLogs.slice(-99), logEntry];
    saveLogsToStorage(globalLogs);

    // Notifica todos os listeners (componentes React)
    globalListeners.forEach(listener => listener(globalLogs));

    // Também loga no console real
    if (type === 'error') console.error(`[DEBUG] ${message}`);
    else if (type === 'warn') console.warn(`[DEBUG] ${message}`);
    else console.log(`[DEBUG] ${message}`);
};

const clearGlobalLogs = () => {
    globalLogs = [];
    sessionStorage.removeItem(STORAGE_KEY);
    globalListeners.forEach(listener => listener(globalLogs));
};

// API global exportada para uso fora de React
export const debugConsole = {
    log: (message) => addGlobalLog(message, 'info'),
    warn: (message) => addGlobalLog(message, 'warn'),
    error: (message) => addGlobalLog(message, 'error'),
    clear: clearGlobalLogs,
    isEnabled: isDebugEnabled
};

// ============================================================
// CONTEXTO REACT - Para uso em componentes
// ============================================================

const DebugContext = createContext(null);

// Hook para usar o debug em qualquer componente
export const useDebug = () => {
    const context = useContext(DebugContext);
    if (!context) {
        // Se não houver provider, usa a API global diretamente
        return {
            log: debugConsole.log,
            warn: debugConsole.warn,
            error: debugConsole.error,
            clear: debugConsole.clear,
            logs: globalLogs,
            isEnabled: isDebugEnabled()
        };
    }
    return context;
};

// ============================================================
// COMPONENTE UI
// ============================================================

const DebugConsoleUI = ({ logs, onClear, enabled }) => {
    const [isMinimized, setIsMinimized] = useState(true);
    const [isHidden, setIsHidden] = useState(false);
    const [filter, setFilter] = useState('');

    if (!enabled) return null;

    // Filtra logs baseado no texto de busca
    const filteredLogs = filter
        ? logs.filter(log => log.message.toLowerCase().includes(filter.toLowerCase()))
        : logs;

    if (isHidden) {
        return (
            <button
                onClick={() => setIsHidden(false)}
                style={{
                    position: 'fixed',
                    bottom: '10px',
                    right: '10px',
                    background: '#222',
                    color: '#0f0',
                    border: '1px solid #0f0',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    cursor: 'pointer',
                    zIndex: 99999,
                    fontSize: '16px'
                }}
                title="Abrir Debug Console"
            >
                🔧
            </button>
        );
    }

    return (
        <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'rgba(0,0,0,0.95)',
            color: '#0f0',
            fontSize: '11px',
            fontFamily: 'monospace',
            zIndex: 99999,
            borderTop: '2px solid #0f0'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '4px 8px',
                background: '#222',
                borderBottom: '1px solid #444'
            }}>
                <span style={{ color: '#ff0' }}>🔧 DEBUG ({filteredLogs.length}/{logs.length})</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {/* Campo de filtro */}
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <span style={{
                            position: 'absolute',
                            left: '6px',
                            color: '#888',
                            fontSize: '12px'
                        }}>🔍</span>
                        <input
                            type="text"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filtrar..."
                            style={{
                                background: '#333',
                                color: '#fff',
                                border: '1px solid #555',
                                borderRadius: '3px',
                                padding: '2px 6px 2px 24px',
                                fontSize: '10px',
                                width: '100px'
                            }}
                        />
                        {filter && (
                            <button
                                onClick={() => setFilter('')}
                                style={{
                                    position: 'absolute',
                                    right: '4px',
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#888',
                                    cursor: 'pointer',
                                    fontSize: '10px',
                                    padding: '0'
                                }}
                            >✕</button>
                        )}
                    </div>
                    <button
                        onClick={onClear}
                        style={{
                            background: '#600',
                            color: '#fff',
                            border: 'none',
                            padding: '2px 8px',
                            cursor: 'pointer',
                            fontSize: '10px',
                            borderRadius: '3px'
                        }}
                    >
                        Limpar
                    </button>
                    <button
                        onClick={() => setIsMinimized(!isMinimized)}
                        style={{
                            background: '#444',
                            color: '#fff',
                            border: 'none',
                            padding: '2px 8px',
                            cursor: 'pointer',
                            fontSize: '10px',
                            borderRadius: '3px'
                        }}
                    >
                        {isMinimized ? '▲' : '▼'}
                    </button>
                    <button
                        onClick={() => setIsHidden(true)}
                        style={{
                            background: '#333',
                            color: '#888',
                            border: 'none',
                            padding: '2px 8px',
                            cursor: 'pointer',
                            fontSize: '10px',
                            borderRadius: '3px'
                        }}
                    >
                        ✕
                    </button>
                </div>
            </div>

            {/* Logs */}
            {!isMinimized && (
                <div style={{
                    padding: '8px',
                    maxHeight: '200px',
                    overflow: 'auto'
                }}>
                    {filteredLogs.length === 0 ? (
                        <div style={{ color: '#666' }}>
                            {filter ? `Nenhum log contém "${filter}"` : 'Nenhum log ainda...'}
                        </div>
                    ) : (
                        filteredLogs.map((log, i) => (
                            <div key={i} style={{
                                marginBottom: '2px',
                                color: log.type === 'error' ? '#f55' : log.type === 'warn' ? '#ff0' : '#0f0',
                                wordBreak: 'break-word'
                            }}>
                                <span style={{ color: '#888' }}>[{log.time}]</span> {log.message}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================================
// PROVIDER - Envolve a aplicação
// ============================================================

export const DebugProvider = ({ children }) => {
    const [enabled, setEnabled] = useState(isDebugEnabled);
    const [logs, setLogs] = useState(() => globalLogs);

    // Registra listener para receber updates da API global
    useEffect(() => {
        const listener = (newLogs) => setLogs([...newLogs]);
        globalListeners.push(listener);

        return () => {
            globalListeners = globalListeners.filter(l => l !== listener);
        };
    }, []);

    // Escuta mudanças no localStorage para enabled
    useEffect(() => {
        const handleStorageChange = () => {
            setEnabled(isDebugEnabled());
        };
        window.addEventListener('storage', handleStorageChange);
        const interval = setInterval(handleStorageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, []);

    const log = useCallback((message) => debugConsole.log(message), []);
    const warn = useCallback((message) => debugConsole.warn(message), []);
    const error = useCallback((message) => debugConsole.error(message), []);
    const clear = useCallback(() => debugConsole.clear(), []);

    return (
        <DebugContext.Provider value={{ log, warn, error, clear, logs, isEnabled: enabled }}>
            {children}
            <DebugConsoleUI logs={logs} onClear={clear} enabled={enabled} />
        </DebugContext.Provider>
    );
};

// Componente standalone (mantido para compatibilidade)
export const DebugConsole = ({ logs = [], onClear = () => { } }) => {
    return <DebugConsoleUI logs={logs} onClear={onClear} enabled={true} />;
};

export default DebugConsole;
